import { motion } from "framer-motion";
import {
  Globe2,
  Boxes,
  Workflow,
  Network,
  Sparkles,
  Lightbulb,
  Brain,
  Building2,
  ArrowDown,
  CheckCircle2,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   Layered architecture
   ───────────────────────────────────────────────────────────────────────── */
const layers = [
  {
    id: "channels",
    name: "Channels Layer",
    items: [
      "Bancassurance",
      "Affinity",
      "Embedded",
      "Digital Direct",
      "APIs",
      "Personalized",
      "Integrated",
      "Incumbent",
      "Industrialized",
      "Intelligent",
    ],
    Icon: Globe2,
    bg: "#F5F7FA",
    text: "#111827",
    sub: "#475569",
    accent: "#64748B",
    border: "#E5E7EB",
    iconBg: "#FFFFFF",
  },
  {
    id: "ai",
    name: "AI / Intelligence Layer",
    items: [
      "Claims AI",
      "Risk Scoring",
      "Fraud Detection",
      "Automation",
      "Predictive Analytics",
    ],
    Icon: Sparkles,
    bg: "#0B1F3A",
    text: "#FFFFFF",
    sub: "#CBD5E1",
    accent: "#C84B31",
    border: "rgba(255,255,255,0.12)",
    iconBg: "rgba(255,255,255,0.08)",
    dark: true,
  },
  {
    id: "ai-decision",
    name: "AI Decision Support Layer",
    items: [
      "Case Summarization",
      "Next-Best Action",
      "Triage",
      "Anomaly Detection",
      "Decision Recommendation",
      "Communication Drafting",
      "SLA Risk Alerts",
      "Portfolio Insights",
    ],
    Icon: Lightbulb,
    bg: "#F3E8FF",
    text: "#581C87",
    sub: "#6D28D9",
    accent: "#7C3AED",
    border: "#E9D5FF",
    iconBg: "#FFFFFF",
  },
  {
    id: "cognition",
    name: "Insurance Cognition Layer",
    items: [
      "Insurance Ontology & Semantic Model",
      "Coverage / Benefit Interpretation",
      "Claims Reasoning Patterns",
      "Regulatory Memory",
      "Explanation Engine with Evidence Traceability",
    ],
    Icon: Brain,
    bg: "#CCFBF1",
    text: "#134E4A",
    sub: "#0F766E",
    accent: "#0D9488",
    border: "#99F6E4",
    iconBg: "#FFFFFF",
  },
  {
    id: "tenant",
    name: "Tenant Intelligence Layer",
    items: [
      "Insurer-Specific Rule Packs & Workflows",
      "Authority Matrix",
      "Templates & Channel Variants",
      "Tenant Memory & Retrieval Partitions",
      "Feedback Learning",
      "Controlled RAFT / RAG / Tool-Use",
    ],
    Icon: Building2,
    bg: "#FEF3C7",
    text: "#78350F",
    sub: "#B45309",
    accent: "#D97706",
    border: "#FDE68A",
    iconBg: "#FFFFFF",
  },
  {
    id: "core",
    name: "Core Platform Layer",
    items: [
      "Policy Administration",
      "Underwriting",
      "Claims",
      "Reinsurance",
      "Accounting",
      "Product Engine",
      "Quotation",
      "Pricing",
      "Billing & Collection",
      "Messaging & Communication",
      "Reports",
    ],
    Icon: Boxes,
    bg: "#E0F2FE",
    text: "#0C4A6E",
    sub: "#1E40AF",
    accent: "#0284C7",
    border: "#BAE6FD",
    iconBg: "#FFFFFF",
  },
  {
    id: "workflow",
    name: "Workflow & Governance",
    items: ["STP / NSTP", "Rule Engine", "Maker–Checker", "Approvals", "Audit Trails"],
    Icon: Workflow,
    bg: "#EEF2FF",
    text: "#3730A3",
    sub: "#4338CA",
    accent: "#6366F1",
    border: "#C7D2FE",
    iconBg: "#FFFFFF",
  },
  {
    id: "data",
    name: "Data & Integration",
    items: ["Open APIs", "Partner Integrations", "Data Exchange", "Real-Time Processing"],
    Icon: Network,
    bg: "#F0FDF4",
    text: "#14532D",
    sub: "#15803D",
    accent: "#22C55E",
    border: "#BBF7D0",
    iconBg: "#FFFFFF",
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Supporting capability points
   ───────────────────────────────────────────────────────────────────────── */
const capabilities = [
  {
    title: "External ecosystem connectivity",
    body:
      "Pre-built connectors to regulator and exchange rails, payment networks, identity verification, fraud and risk utilities — keeping integration friction low.",
  },
  {
    title: "Regulator intelligence as input",
    body:
      "Regulatory and exchange signals flow into the platform as inputs, not dependencies — insurer-specific cognition stays the differentiator, while PAS execution remains the authority.",
  },
  {
    title: "Tenant-native configuration",
    body:
      "Insurer-specific rule packs, authority matrices, templates and channel variants — isolated per tenant and tunable without touching the core.",
  },
  {
    title: "Enterprise-grade controls",
    body:
      "Model and prompt registry, guardrails, PII masking, role-based access, human approval gates, logs and observability across every layer.",
  },
];

export const Architecture = () => {
  return (
    <section
      id="architecture"
      data-testid="architecture-section"
      className="bg-white"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 md:mb-16 max-w-3xl mx-auto"
        >
          <div className="mb-6">
            <span className="overline text-copper">Platform Architecture</span>
          </div>
          <h2
            data-testid="architecture-title"
            className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.1] text-ink mb-5"
          >
            Built for Scale.{" "}
            <span className="text-copper">Designed for Control.</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[800px] mx-auto">
            A modular, API-first architecture with AI embedded across the
            lifecycle — enabling seamless integration, real-time processing and
            enterprise-grade governance.
          </p>
        </motion.div>

        {/* LAYER STACK */}
        <div className="max-w-[1100px] mx-auto space-y-3 md:space-y-4">
          {layers.map((l, i) => (
            <div key={l.id}>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="relative rounded-[14px] px-5 md:px-6 py-5 md:py-6 transition-shadow hover:shadow-md flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
                style={{
                  background: l.bg,
                  border: `1px solid ${l.border}`,
                  color: l.text,
                }}
                data-testid={`architecture-layer-${l.id}`}
              >
                {/* Label */}
                <div className="flex items-center gap-4 md:w-[280px] shrink-0">
                  <div
                    className="h-11 w-11 rounded-md flex items-center justify-center shrink-0"
                    style={{
                      background: l.iconBg,
                      border: `1px solid ${l.border}`,
                    }}
                  >
                    <l.Icon
                      className="h-5 w-5"
                      style={{ color: l.accent }}
                      strokeWidth={1.7}
                    />
                  </div>
                  <div>
                    <div className="overline mb-1" style={{ color: l.dark ? "rgba(255,255,255,0.55)" : l.sub }}>
                      Layer 0{i + 1}
                    </div>
                    <div
                      className="font-display text-[17px] md:text-[19px] tracking-tight leading-tight font-medium"
                      style={{ color: l.text }}
                    >
                      {l.name}
                    </div>
                  </div>
                </div>

                {/* Pills */}
                <div className="flex flex-wrap items-center gap-2 md:gap-2.5 flex-1">
                  {l.items.map((it, k) => (
                    <span
                      key={it}
                      className="text-[12px] md:text-[13px] px-3 py-1.5 rounded-full"
                      style={{
                        background: l.dark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
                        color: l.dark ? "#E2E8F0" : l.sub,
                        border: l.dark
                          ? "1px solid rgba(255,255,255,0.12)"
                          : `1px solid ${l.border}`,
                      }}
                    >
                      {it}
                      {k < l.items.length - 1 && (
                        <span className="sr-only"> | </span>
                      )}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Connector arrow */}
              {i < layers.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.5 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.08, duration: 0.4 }}
                  className="flex justify-center py-1.5"
                  aria-hidden
                >
                  <ArrowDown className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                </motion.div>
              )}
            </div>
          ))}
        </div>

        {/* CAPABILITY POINTS */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 md:mt-20 max-w-[1100px] mx-auto"
        >
          <div className="flex items-center mb-8 justify-center">
            <span className="overline text-slate-500">Supporting Capabilities</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-7">
            {capabilities.map((c, i) => (
              <div key={c.title} className="flex items-start gap-3" data-testid={`capability-${i}`}>
                <CheckCircle2 className="h-5 w-5 text-copper shrink-0 mt-0.5" strokeWidth={1.7} />
                <div>
                  <div className="font-display text-[15px] md:text-base text-ink leading-snug mb-1 font-medium">
                    {c.title}
                  </div>
                  <p className="text-[13px] md:text-[14px] text-slate-600 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
